import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    SheetPanelDynamicStyleType,
    SheetPanelAxisConstantType
} from "./type";
import type {
    Mutable
} from "../../../../types";
import type {
    SheetEdgeType
} from "../../type";
import {
    getSurfaceStyle
} from "../../../../helpers/theme/elevation";
import {
    concentric
} from "../../../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../../../helpers/theme/corner";

export const SHEET_PANEL_EDGE_STYLES: Record<SheetEdgeType, "bottomEdge" | "startEdge" | "topEdge" | "endEdge"> = {
    bottom: "bottomEdge",
    start: "startEdge",
    top: "topEdge",
    end: "endEdge"
};

export const SHEET_PANEL_BORDER_KEYS: Record<SheetEdgeType, keyof ViewStyle> = {
    bottom: "borderTopWidth",
    top: "borderBottomWidth",
    start: "borderEndWidth",
    end: "borderStartWidth"
};

export const SHEET_PANEL_RADIUS_KEYS: Record<SheetEdgeType, Array<keyof ViewStyle>> = {
    top: [
        "borderBottomStartRadius",
        "borderBottomEndRadius"
    ],
    end: [
        "borderTopStartRadius",
        "borderBottomStartRadius"
    ],
    bottom: [
        "borderTopStartRadius",
        "borderTopEndRadius"
    ],
    start: [
        "borderTopEndRadius",
        "borderBottomEndRadius"
    ]
};

export const SHEET_GESTURE_CLOSE_RATIO = 1 / 3;

export const SHEET_GESTURE_FAST_VELOCITY = 0.5;

export const SHEET_GESTURE_SETTLE_FRAME = 50;

export const getSheetPanelAxis = ({
    directionSign,
    edge
}: SheetPanelAxisConstantType): number => {
    if(edge === "end") {
        return directionSign;
    }

    if(edge === "start") {
        return -directionSign;
    }

    return edge === "top" ? -1 : 1;
};

const stylesheet = StyleSheet.create({
    container: {
        flexDirection: "column",
        position: "absolute",
        borderStyle: "solid",
        minHeight: 0,
        minWidth: 0
    },
    clip: {
        flexDirection: "column",
        overflow: "hidden",
        flexShrink: 1,
        flexGrow: 1
    },
    endEdge: {
        bottom: 0,
        end: 0,
        top: 0
    },
    startEdge: {
        bottom: 0,
        start: 0,
        top: 0
    },
    bottomEdge: {
        bottom: 0,
        start: 0,
        end: 0
    },
    topEdge: {
        start: 0,
        end: 0,
        top: 0
    }
});

export const useStyles = ({
    shapeConfigs,
    shadowAlpha,
    elevations,
    maxHeight,
    radiuses,
    borders,
    colors,
    width,
    edge
}: SheetPanelDynamicStyleType) => {
    const isHorizontal = edge === "start" || edge === "end";

    const surface = {
        ...getSurfaceStyle({
            surface: "sheet",
            shadowAlpha,
            elevations,
            borders,
            colors
        }),
        borderWidth: 0
    } as Mutable<ViewStyle>;

    const clip = {} as Mutable<ViewStyle>;

    const {
        borderRadius: surfaceRadius,
        ...surfaceShape
    } = getCornerStyle({
        radius: radiuses.surface,
        shapeConfigs
    });

    const {
        borderRadius: clipRadius,
        ...clipShape
    } = getCornerStyle({
        radius: concentric({
            outer: radiuses.surface,
            role: radiuses.surface,
            inset: borders.line
        }),
        shapeConfigs
    });

    Object.assign(surface, surfaceShape);

    Object.assign(clip, clipShape);

    Object.assign(surface, {
        [SHEET_PANEL_BORDER_KEYS[edge]]: borders.line
    });

    SHEET_PANEL_RADIUS_KEYS[edge].forEach((key) => {
        Object.assign(surface, {
            [key]: surfaceRadius
        });

        Object.assign(clip, {
            [key]: clipRadius
        });
    });

    if(isHorizontal) {
        surface.width = width;
    } else {
        surface.maxHeight = maxHeight;
    }

    return {
        surface,
        clip
    };
};
export default stylesheet;
